<template>
  <v-card
    class="d-flex flex-column"
    flat
    tile
    :height="!isMobileBreakpoint ? 548 : '100dvh'"
  >
    <div class="w-full d-flex justify-space-between pa-6">
      <div class="d-flex align-start">
        <v-btn icon @click="handleBack" class="mr-2">
          <v-icon class="neutral-70--text">mdi-arrow-left</v-icon>
        </v-btn>
        <div v-if="!isMobileBreakpoint" class="d-flex flex-column">
          <span class="body-bold-large neutral-20-dark--text">
            {{ $t('message.ai.dubbings.selectDubbingTech.title') }}
          </span>
          <span class="body-regular-small neutral-40-light--text">
            {{ $t('message.ai.dubbings.selectDubbingTech.subtitle') }}
            <a
              target="_blank"
              size="xsmall"
              type="secondary"
              :href="$t('message.ai.dubbings.selectDubbingTech.learnMoreHref')"
            >
              {{ $t('message.ai.dubbings.selectDubbingTech.learnMore') }}
              <v-icon size="12">mdi-open-in-new</v-icon>
            </a>
          </span>
        </div>
      </div>
      <v-btn icon @click="handleClose">
        <v-icon class="neutral-70--text">mdi-close</v-icon>
      </v-btn>
    </div>

    <v-card-text style="overflow-y: auto" class="flex-grow-1 px-7 py-0">
      <div v-if="isMobileBreakpoint" class="d-flex flex-column mb-4">
        <span class="body-bold-large neutral-20-dark--text">
          {{ $t('message.ai.dubbings.selectDubbingTech.title') }}
        </span>
        <span class="body-regular-small neutral-40-light--text">
          {{ $t('message.ai.dubbings.selectDubbingTech.subtitle') }}
          <a
            target="_blank"
            size="xsmall"
            type="secondary"
            :href="$t('message.ai.dubbings.selectDubbingTech.learnMoreHref')"
          >
            {{ $t('message.ai.dubbings.selectDubbingTech.learnMore') }}
            <v-icon size="12">mdi-open-in-new</v-icon>
          </a>
        </span>
      </div>

      <v-radio-group v-model="selectedTech" class="mt-0 pt-0">
        <div v-for="(option, index) in dubbingOptions" :key="index">
          <v-row
            :key="option.value"
            no-gutters
            class="option-container px-5 py-3"
            :class="{
              'dubbing-option--disabled':
                radioDisabled[index] && selectedTech !== option.value,
            }"
            @click="
              !autoSelectedTech &&
                !radioDisabled[index] &&
                (selectedTech = option.value)
            "
          >
            <v-col cols="auto" class="d-flex align-center">
              <v-radio
                :value="option.value"
                :disabled="
                  radioDisabled[index] && selectedTech !== option.value
                "
              ></v-radio>
            </v-col>
            <v-col class="flex-grow-1 d-flex flex-column align-start">
              <p class="body-bold-small neutral-20-dark--text mb-0">
                {{ option.title }}
              </p>

              <v-tooltip
                v-if="radioDisabled[index] && selectedTech !== option.value"
                bottom
                open-on-hover
                :nudge-bottom="10"
              >
                <template v-slot:activator="{ on, attrs }">
                  <p
                    class="body-regular-small neutral-40-light--text mb-3"
                    style="word-break: break-word"
                    v-on="on"
                    v-bind="attrs"
                  >
                    {{ option.description }}
                  </p>
                </template>
                <span
                  style="
                    max-width: 225px;
                    display: block;
                    white-space: pre-line;
                  "
                >
                  {{ dubbingTechTooltip(index) }}
                </span>
              </v-tooltip>

              <p
                v-else
                class="body-regular-small neutral-40-light--text mb-3"
                style="word-break: break-word"
              >
                {{ option.description }}
              </p>
              <div class="d-flex flex-wrap" style="gap: 8px">
                <v-tag
                  v-for="(tag, i) in option.tags"
                  :key="i"
                  type="neutral"
                  class="py-0 px-1"
                  style="height: 18px"
                >
                  <span class="body-regular-smaller">{{ tag }}</span>
                </v-tag>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-radio-group>
    </v-card-text>

    <div class="card-actions">
      <base-btn
        type="tertiary"
        @click="handleClose"
        :style="{ width: isMobileBreakpoint ? '50%' : 'auto' }"
      >
        {{ $t('message.actions.cancel') }}
      </base-btn>
      <base-btn
        class="ml-3"
        :disabled="!selectedTech"
        @click="handleContinue"
        :style="{ width: isMobileBreakpoint ? '50%' : 'auto' }"
      >
        {{ $t('message.actions.continue') }}
      </base-btn>
    </div>
  </v-card>
</template>

<script>
import i18n from '@/translations/lang'
import { mapState } from 'vuex'

export default {
  name: 'SelectDubbingTechTemplate',
  props: {
    videos: {
      type: Array,
      required: true,
      default: () => [],
    },
    aiworks: {
      type: Array,
      default: () => [],
    },
    tech: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    selectedTech: '',
    autoSelectedTech: false,
    dubbingOptions: [
      {
        value: 'elevenlabs',
        title: i18n.t('message.ai.dubbings.selectDubbingTech.dubbingAI'),
        description: i18n.t(
          'message.ai.dubbings.selectDubbingTech.dubbingAIDescription'
        ),
        tags: [
          i18n.t('message.ai.dubbings.selectDubbingTech.creditsPerMin', {
            credits: 2,
            plural: 's',
          }),
          // i18n.t('message.ai.dubbings.selectDubbingTech.techElevenLabs'),
        ],
      },
      {
        value: 'heygen',
        title: i18n.t('message.ai.dubbings.selectDubbingTech.dubbingAILipSync'),
        description: i18n.t(
          'message.ai.dubbings.selectDubbingTech.dubbingAILipSyncDescription'
        ),
        tags: [
          i18n.t('message.ai.dubbings.selectDubbingTech.creditsPerMin', {
            credits: 10,
            plural: 's',
          }),
          // i18n.t('message.ai.dubbings.selectDubbingTech.techHeyGen'),
        ],
      },
    ],
  }),
  computed: {
    ...mapState({
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    singleVideo() {
      return this.videos.length === 1 ? this.videos[0] : null
    },
    hasPendingLipSync() {
      const selectedVideoIds = new Set(this.videos.map((v) => v.id))
      const lipsyncWorks = this.aiworks?.filter(
        (work) =>
          work.metadata?.is_lipsync === true &&
          selectedVideoIds.has(work.video_id)
      )
      return lipsyncWorks.length > 0
    },
    lipsyncAudios() {
      if (!this.singleVideo) return []
      return (this.singleVideo.config?.lipsync_info ?? []).filter(
        (item) => !item.original
      )
    },
    videosWithAudios() {
      return this.videos.filter((video) => {
        const hasGeneratedAudios = (video.audios ?? []).some(
          (a) => a.type === 'GENERATED'
        )
        const hasLipSyncAudios = (video.config?.lipsync_info ?? []).some(
          (item) => !item.original
        )
        return hasGeneratedAudios || hasLipSyncAudios
      })
    },
    videosWithLipSync() {
      return this.videos.filter((video) => {
        const hasLipSyncAudios = (video.config?.lipsync_info ?? []).some(
          (item) => !item.original
        )
        return hasLipSyncAudios
      })
    },
    videosWithNormalAudios() {
      return this.videos.filter((video) => {
        const hasGeneratedAudios = (video.audios ?? []).some((a) => {
          if (a.type !== 'GENERATED') return false

          const lipsyncInfo = video.config?.lipsync_info ?? []
          const isLipsync = lipsyncInfo.some(
            (item) => !item.original && item.srclang === a.lang
          )

          return !isLipsync
        })
        return hasGeneratedAudios
      })
    },
    multiVideoTech() {
      const lipsyncVideoIds = new Set(
        this.aiworks?.map((work) => work.video_id)
      )

      const relevantVideos = this.videos.filter(
        (video) =>
          (video.audios ?? []).some((a) => a.type === 'GENERATED') ||
          lipsyncVideoIds.has(video.id)
      )

      if (relevantVideos.length === 0) {
        return null
      }

      const allAreLipsync = relevantVideos.every((video) =>
        lipsyncVideoIds.has(video.id)
      )
      if (allAreLipsync) {
        return 'heygen'
      }

      const allHaveAudio = relevantVideos.every(
        (video) =>
          (video.audios ?? []).some((a) => a.type === 'GENERATED') &&
          !lipsyncVideoIds.has(video.id)
      )
      if (allHaveAudio) {
        return 'elevenlabs'
      }

      return null
    },
    hasVideoOverDurationLimit() {
      return this.videos.some((video) => {
        const minutes = Math.floor(video.length / 60)
        const seconds = Math.floor(video.length % 60)
        const totalMinutes = seconds > 0 ? minutes + 1 : minutes
        return totalMinutes > 30
      })
    },
    radioDisabled() {
      if (this.hasVideoOverDurationLimit) {
        return [false, true]
      }
      if (!this.singleVideo) {
        const tech = this.multiVideoTech
        if (tech === 'heygen') return [true, false]
        if (tech === 'elevenlabs') return [false, true]
        return [false, false]
      }

      if (!this.hasAnyAudio) return [false, false]

      if (this.hasPendingLipSync || this.hasLipSyncAudio) return [true, false]
      if (this.generatedAudios.length > 0) return [false, true]

      const arr = [false, false]
      if (arr[0] && arr[1]) return [false, true]
      return arr
    },
    hasLipSyncAudio() {
      if (!this.singleVideo) return false

      const lipsyncInfo = this.singleVideo.config?.lipsync_info ?? []
      const audios = this.singleVideo.audios ?? []

      const nonOriginalLipsync = lipsyncInfo.filter((item) => !item.original)

      const hasCorrespondingAudios = nonOriginalLipsync.some((lipsyncItem) => {
        return audios.some((audio) => {
          return (
            audio.lang === lipsyncItem.srclang ||
            audio.video_external_id === lipsyncItem.video_external_id
          )
        })
      })

      return hasCorrespondingAudios
    },
    generatedAudios() {
      if (!this.singleVideo) return []
      return (this.singleVideo.audios ?? []).filter(
        (a) => a.type === 'GENERATED' || a.type === 'UPLOADED'
      )
    },
    hasAnyAudio() {
      return (
        this.generatedAudios.length > 0 ||
        this.hasLipSyncAudio ||
        this.hasPendingLipSync
      )
    },
    dubbingTechTooltip() {
      return (index) => {
        const heygenIndex = 1
        if (index === heygenIndex && this.hasVideoOverDurationLimit) {
          return this.$t(
            'message.ai.dubbings.existingSubtitles.radioAlertDurationLimit'
          )
        }
        return this.$t(
          'message.ai.dubbings.existingSubtitles.radioAlertAlreadyDubbed'
        )
      }
    },
  },
  watch: {
    videos: {
      handler() {
        this.updateSelectedTech()
      },
      deep: true,
    },
    aiworks: {
      handler() {
        this.updateSelectedTech()
      },
      deep: true,
    },
  },
  mounted() {
    if (!this.tech) {
      this.updateSelectedTech()
    } else {
      this.selectedTech = this.tech
      this.autoSelectedTech = false
    }
  },
  methods: {
    handleClose() {
      this.$root.$emit('closeDubbingModal')
    },
    handleBack() {
      this.$root.$emit('backDubbingInitialScreen')
    },
    handleContinue() {
      if (this.selectedTech) {
        this.$emit('selectedDubbingTech', this.selectedTech)
      }
    },
    updateSelectedTech() {
      if (this.selectedTech) return

      if (this.hasVideoOverDurationLimit) {
        this.selectedTech = 'elevenlabs'
        this.autoSelectedTech = true
        return
      }

      if (!this.singleVideo) {
        const tech = this.multiVideoTech
        if (tech) {
          this.selectedTech = tech
          this.autoSelectedTech = true
        } else {
          this.selectedTech = ''
          this.autoSelectedTech = false
        }
        return
      }

      if (this.hasPendingLipSync) {
        this.selectedTech = 'heygen'
        this.autoSelectedTech = true
      } else if (this.hasLipSyncAudio && this.hasAnyAudio) {
        this.selectedTech = 'heygen'
        this.autoSelectedTech = true
      } else if (this.generatedAudios.length > 0) {
        this.selectedTech = 'elevenlabs'
        this.autoSelectedTech = true
      } else {
        this.selectedTech = ''
        this.autoSelectedTech = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.option-container {
  cursor: pointer;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  padding: 28px;
  border-top: 1px solid varFigma(neutral-95-lightest);
  background: varFigma(neutral-100-white);
}

.bg-dark {
  .card-actions {
    border-top: 1px solid varFigma(neutral-95-lightest);
    background: varFigma(neutral-100-white);
  }
}

a[type='secondary']:active,
a[type='secondary']:visited {
  color: varFigma(neutral-30-base) !important;
}

@media (max-width: 768px) {
  .card-actions {
    padding: 16px 20px;
  }
}

.dubbing-option--disabled {
  color: varFigma(neutral-70) !important;
  cursor: default !important;

  .v-tag,
  .body-bold-small,
  .body-regular-small,
  .body-regular-smaller {
    color: varFigma(neutral-70) !important;
  }
}
</style>
